/* What a pack and a framework mean on screen, decided once.
 *
 * The judgement that matters here is signed-versus-unsigned: an unsigned pack is not a broken pack —
 * the hashes hold, the origin is unproven, mirroring UNSEALED audit days — and a screen that painted
 * it red would teach operators that unsigned means invalid, which is precisely the collapse the
 * backend's four-way SealCheck exists to prevent. So `signingOf` is a two-word vocabulary and each
 * tone carries a word (WCAG 1.4.1, as everywhere else).
 *
 * The second judgement is that **a narrower framework is not an incomplete one.** Every count of
 * evidence kinds is reported against six, because a framework naming two kinds is a framework about
 * two things — not one that forgot four. `EVIDENCE_KINDS` is the closed vocabulary the API publishes,
 * so the denominator is derived from it rather than typed as a literal.
 */

import { EVIDENCE_KINDS } from '@/shared/api/generated/compliance';
import type {
  EvidenceKind,
  EvidencePack,
  Framework,
} from '@/shared/api/generated/compliance';

export type SigningState = 'signed' | 'unsigned';

export function signingOf(pack: EvidencePack): SigningState {
  return pack.signature === null ? 'unsigned' : 'signed';
}

export function signingLabelKey(pack: EvidencePack): string {
  return `compliance.signing.${signingOf(pack)}`;
}

/** The tone each signing state takes. Total, so a third state is a compile error here rather than a
 * chip rendered in whatever hue a fallback picked.
 *
 * **`info` for unsigned, never `warn`.** A pack with no Transit key configured is not a fault in the
 * pack, and amber would put every pack on an air-gapped install into a queue people learn to skim.
 */
export const SIGNING_TONE = {
  signed: 'ok',
  unsigned: 'info',
} as const satisfies Record<SigningState, 'ok' | 'info'>;

/** What the chip's `title` says. For a signed pack that is the key that signed it — a fact an auditor
 * checks — and for an unsigned one it is the sentence explaining that absence is honest. */
export function signingTitle(pack: EvidencePack): { readonly key: string | null } {
  return { key: signingOf(pack) === 'signed' ? pack.signing_key : null };
}

/** How much of a checksum to show. Sixteen hex characters is enough to compare against a
 * verification tool's output and short enough to read; the full value is on the `title` and travels
 * with the download, which is the copy the screen carries. */
export const CHECKSUM_VISIBLE = 16;

export function shortChecksum(checksum: string): string {
  return `${checksum.slice(0, CHECKSUM_VISIBLE)}…`;
}

/** Newest first. Packs are point-in-time artifacts; the latest is the one an audit starts from. */
export function orderPacks(packs: readonly EvidencePack[]): EvidencePack[] {
  return [...packs].sort((left, right) =>
    right.generated_at.localeCompare(left.generated_at),
  );
}

/** A framework's identity is `(code, version)`, so its key is both.
 *
 * Both halves, because a pack is always generated against one framework *at one version* and the
 * version is part of what it evidences — two versions of one code are two different frameworks for
 * every purpose on this screen.
 */
export function frameworkKey(framework: Pick<Framework, 'code' | 'version'>): string {
  return `${framework.code}@${framework.version}`;
}

export function findFramework(
  frameworks: readonly Framework[],
  code: string,
  version: string,
): Framework | undefined {
  return frameworks.find(
    (framework) => framework.code === code && framework.version === version,
  );
}

/** The distinct evidence kinds a framework's controls draw on, in the API's published order.
 *
 * Ordered by `EVIDENCE_KINDS` rather than by first appearance in the controls, so two frameworks
 * listing the same kinds render them in the same order — otherwise the chips reshuffle between
 * frameworks for reasons that are about control authoring rather than about evidence.
 */
export function evidenceKindsOf(framework: Framework): readonly EvidenceKind[] {
  const used = new Set(framework.controls.flatMap((control) => [...control.evidence]));
  return EVIDENCE_KINDS.filter((kind) => used.has(kind));
}

export function evidenceKindCount(framework: Framework): number {
  return evidenceKindsOf(framework).length;
}

/** The denominator on every "N of 6". Derived, so adding a seventh kind to the contract moves every
 * figure on the screen rather than leaving a stale literal behind. */
export const EVIDENCE_KIND_TOTAL = EVIDENCE_KINDS.length;

/** How many of a framework's controls name each kind — including the kinds it does not name at all.
 *
 * The zero entries are the point: the detail screen lists all six and says which this framework does
 * not use, because "not used here" is a fact about the framework and a blank row is a fact about
 * nothing.
 */
export function kindUsage(framework: Framework): ReadonlyMap<EvidenceKind, number> {
  const usage = new Map<EvidenceKind, number>();
  for (const kind of EVIDENCE_KINDS) usage.set(kind, 0);
  for (const control of framework.controls) {
    for (const kind of control.evidence) {
      usage.set(kind, (usage.get(kind) ?? 0) + 1);
    }
  }
  return usage;
}

/* ---------------------------------------------------------------- the generate form */

export interface PackDraft {
  /** `frameworkKey` of the chosen framework, or `''` for none. */
  readonly framework: string;
  readonly periodStart: string;
  readonly periodEnd: string;
}

/** A period is a pair of dates in order.
 *
 * Compared as strings, which is exact rather than lazy: an `<input type="date">` yields ISO
 * `YYYY-MM-DD`, and that format sorts lexicographically the same way it sorts chronologically. A
 * `Date` round trip would introduce a timezone into a comparison that has no time in it.
 */
export function periodIsValid(draft: PackDraft): boolean {
  return (
    draft.periodStart !== '' &&
    draft.periodEnd !== '' &&
    draft.periodStart <= draft.periodEnd
  );
}

export function packDraftIsValid(
  draft: PackDraft,
  chosen: Framework | undefined,
): boolean {
  return chosen !== undefined && periodIsValid(draft);
}

/** What the hint under the form says: the blocking reason, or what generating will actually read.
 *
 * The third sentence is the one that earns the hint. Generating is not a query — it reads the audit
 * chain and the collectors as they stand at that moment and writes bytes — so saying how many
 * evidence kinds across how many controls, over which dates, is what lets somebody check they asked
 * for the period they meant before spending it.
 */
export function packHintKey(draft: PackDraft, chosen: Framework | undefined): string {
  if (chosen === undefined) return 'compliance.hint.chooseFramework';
  if (!periodIsValid(draft)) return 'compliance.hint.badPeriod';
  return 'compliance.hint.willRead';
}

/* ---------------------------------------------------------------- instants on screen

 * **The API sends ISO-8601 and the reference renders `12 Mar 2026`.** Both were reaching the screen
 * raw — `2026-08-31T13:47:45.301410+00:00` in a Registered column, which is a machine's format in a
 * cell a person reads, and the same defect §9 records for the profiler's `computed_at`.
 *
 * Two formats, because the reference uses two and the difference is meaningful. A framework is
 * registered on a *day* — `12 Mar 2026` — and nobody needs the second it happened. A pack is
 * generated at an *instant* — `3 Aug 2026 09:20` — because two packs over the same period are told
 * apart by when each was made.
 *
 * `en-GB` explicitly rather than the visitor's locale, matching the reference's `12 Mar 2026`: this
 * platform's market writes day-first, and a US locale would render `Mar 12, 2026` for the same value.
 * The full ISO string goes on a `title` wherever these are used, so the exact instant an auditor may
 * need is one hover away rather than gone.
 */

const DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DAY_AND_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** An unparseable instant renders as itself.
 *
 * Never as "Invalid Date" and never as blank: the API's own value is more use to somebody debugging
 * than either, and a blank cell would read as a field the server did not send.
 */
function formatted(value: string, using: Intl.DateTimeFormat): string {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? value : using.format(at);
}

export function asDay(value: string): string {
  return formatted(value, DAY);
}

export function asInstant(value: string): string {
  return formatted(value, DAY_AND_TIME);
}
